import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Search, Crown, Ban, Coins, ShieldCheck, MapPin } from "lucide-react";
import { API } from "../config";
import { fmtShort } from "./AdminInbox";

const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("session_token")}` });
const fmtFull = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "—");

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-zinc-900 text-sm">
      <span className="text-zinc-500 shrink-0">{label}</span>
      <span className="text-right break-all">{value ?? "—"}</span>
    </div>
  );
}

function MemberActions({ m, onAction }) {
  const [amount, setAmount] = useState(10);
  const btn = "px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1";
  return (
    <div className="space-y-2" data-testid="member-actions">
      <div className="flex items-center gap-2 flex-wrap">
        <Coins className="w-4 h-4 text-yellow-400" />
        <input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="w-20 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm" data-testid="member-credits-amount" />
        <button className={`${btn} bg-emerald-600`} onClick={() => onAction({ action: "credits", amount: Math.abs(amount) })} data-testid="member-add-credits">+ Ajouter</button>
        <button className={`${btn} bg-zinc-700`} onClick={() => onAction({ action: "credits", amount: -Math.abs(amount) })} data-testid="member-remove-credits">− Retirer</button>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Crown className="w-4 h-4 text-yellow-400" />
        <button className={`${btn} bg-yellow-500 text-black`} onClick={() => onAction({ action: "premium", days: 7 })} data-testid="member-premium-7">Offrir Premium 7 j</button>
        <button className={`${btn} bg-yellow-500 text-black`} onClick={() => onAction({ action: "premium", days: 30 })} data-testid="member-premium-30">Offrir Premium 30 j</button>
        {m.is_premium && <button className={`${btn} bg-zinc-700`} onClick={() => onAction({ action: "remove_premium" })} data-testid="member-remove-premium">Retirer Premium</button>}
      </div>
      {!m.is_admin && (
        <div className="flex items-center gap-2">
          <Ban className="w-4 h-4 text-red-400" />
          {m.is_banned
            ? <button className={`${btn} bg-zinc-700`} onClick={() => onAction({ action: "unban" })} data-testid="member-unban">Débloquer le compte</button>
            : <button className={`${btn} bg-red-700`} onClick={() => window.confirm(`Bloquer le compte de ${m.name} ?`) && onAction({ action: "ban" })} data-testid="member-ban">Bloquer le compte</button>}
        </div>
      )}
    </div>
  );
}

export function MemberDrawer({ memberId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    fetch(`${API}/admin/members/${memberId}`, { headers: authHeaders() }).then((r) => r.json()).then(setData);
  }, [memberId]);

  const act = async (body) => {
    const r = await fetch(`${API}/admin/members/${memberId}/action`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
    const d = await r.json();
    if (r.ok) { setData(d); toast.success("Action effectuée"); } else toast.error(d.detail || "Erreur");
  };

  const m = data?.member;
  const sub = m?.subscription;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex justify-end" onClick={onClose} data-testid="member-drawer-overlay">
      <div className="w-full max-w-md h-full overflow-y-auto bg-zinc-950 border-l border-zinc-800 p-4" onClick={(e) => e.stopPropagation()} data-testid="member-drawer">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold">{m?.is_ai_profile ? "Profil IA" : "Fiche membre"}</h2>
          <button onClick={onClose} data-testid="member-drawer-close"><X className="w-5 h-5 text-zinc-400" /></button>
        </div>
        {!m ? <Loader2 className="w-6 h-6 animate-spin text-pink-500 mx-auto mt-10" /> : (
          <div className="space-y-4">
            <div className="flex gap-2 overflow-x-auto" data-testid="member-photos">
              {(m.photos?.length ? m.photos : [m.main_photo]).filter(Boolean).map((p, i) => <img key={i} src={p} alt="" className="w-28 h-36 rounded-xl object-cover shrink-0" />)}
              {!m.photos?.length && !m.main_photo && <div className="w-28 h-36 rounded-xl bg-zinc-900 flex items-center justify-center text-zinc-600 text-xs">Aucune photo</div>}
            </div>
            <div>
              <p className="text-xl font-bold flex items-center gap-2" data-testid="member-name">
                {m.name}{m.age ? `, ${m.age}` : ""} {m.is_verified && <ShieldCheck className="w-4 h-4 text-green-500" />}
                {m.is_premium && <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500 text-black">PREMIUM</span>}
                {m.is_banned && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-700">BLOQUÉ</span>}
              </p>
              <p className="text-sm text-zinc-400 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{m.city || m.location || "—"} · pays détecté : {data.country_detected}</p>
              {m.bio && <p className="mt-2 text-sm text-zinc-300 whitespace-pre-wrap" data-testid="member-bio">{m.bio}</p>}
              {m.bio_en && <p className="mt-1 text-xs text-zinc-500 italic">EN : {m.bio_en}</p>}
              {!!m.interests?.length && <div className="mt-2 flex flex-wrap gap-1">{m.interests.map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800">{t}</span>)}</div>}
            </div>
            {!m.is_ai_profile && <MemberActions m={m} onAction={act} />}
            <div className="grid grid-cols-4 gap-2 text-center" data-testid="member-stats">
              {[["Crédits", m.credits], ["Likes reçus", data.stats.likes_received], ["Likes donnés", data.stats.likes_sent], ["Matchs", data.stats.matches],
                ["Msg envoyés", data.stats.messages_sent], ["Msg reçus", data.stats.messages_received], ["Déblocages", data.stats.conversations_unlocked], ["Vues profil", data.stats.profile_views]]
                .map(([l, v]) => <div key={l} className="bg-zinc-900 rounded-lg py-2"><p className="font-bold text-pink-400">{v ?? 0}</p><p className="text-[10px] text-zinc-500">{l}</p></div>)}
            </div>
            <div>
              <Row label="Email" value={m.email} />
              <Row label="ID" value={m.user_id} />
              <Row label="Genre / cherche" value={`${m.gender || "—"} / ${m.interested_in || m.looking_for || "—"}`} />
              <Row label="Profession" value={m.profession} />
              <Row label="Inscription" value={fmtFull(m.created_at)} />
              <Row label="Dernière activité" value={fmtFull(m.last_active || m.last_login)} />
              <Row label="Connexion via" value={m.auth_provider || (m.google_id ? "Google" : "Email")} />
              <Row label="Messages gratuits restants" value={m.free_messages_today} />
              <Row label="Abonnement" value={sub?.is_active ? `${sub.plan} jusqu'au ${fmtFull(sub.expires_at)} (${sub.source || ""})` : "Aucun"} />
              {m.is_ai_profile && <Row label="Lieux" value={`FR : ${m.city} · UK : ${m.locations?.UK?.city || "—"} · USA : ${m.locations?.US?.city || "—"}`} />}
              {m.is_ai_profile && <Row label="Physique" value={`Cheveux ${m.hair} · Yeux ${m.eyes} · ${m.body_type || ""}`} />}
            </div>
            {!m.is_ai_profile && (
              <div data-testid="member-purchases">
                <p className="text-sm font-semibold mb-1">Achats & crédits ({data.purchases.length})</p>
                {data.purchases.length === 0 && <p className="text-xs text-zinc-500">Aucun achat</p>}
                {data.purchases.map((p, i) => (
                  <div key={i} className="flex justify-between text-xs py-1 border-b border-zinc-900">
                    <span>{p.sku || p.type || p.package || "—"} <span className="text-zinc-500">· {p.source || ""}</span></span>
                    <span className="text-zinc-400">{p.credits ?? p.credits_added ?? ""} · {fmtShort(p.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const MEMBER_FILTERS = [["all", "Tous"], ["today", "Inscrits aujourd'hui"], ["premium", "Premium"], ["buyers", "Acheteurs"], ["banned", "Bloqués"]];

export function MembersTab({ initialFilter = "all", onOpenMember }) {
  const [filter, setFilter] = useState(initialFilter);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ total: 0, items: [] });
  useEffect(() => { setFilter(initialFilter); }, [initialFilter]);
  useEffect(() => {
    const t = setTimeout(() => {
      fetch(`${API}/admin/members?filter=${filter}&search=${encodeURIComponent(search)}&skip=${page * 50}&limit=50`, { headers: authHeaders() })
        .then((r) => r.json()).then(setData);
    }, 250);
    return () => clearTimeout(t);
  }, [filter, search, page]);
  return (
    <div className="p-3" data-testid="admin-members">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2 bg-zinc-900 rounded-full px-3 py-1.5 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-zinc-500" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Nom, email, ville, ID…" className="bg-transparent text-sm flex-1 outline-none" data-testid="members-search" />
        </div>
        {MEMBER_FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => { setFilter(k); setPage(0); }} data-testid={`members-filter-${k}`}
            className={`px-3 py-1 rounded-full text-xs ${filter === k ? "bg-pink-600" : "bg-zinc-900 text-zinc-400"}`}>{l}</button>
        ))}
      </div>
      <p className="text-xs text-zinc-500 mb-2" data-testid="members-total">{data.total} membre(s) · cliquez pour ouvrir la fiche</p>
      <div className="divide-y divide-zinc-900">
        {data.items.map((u, i) => (
          <button key={u.user_id} onClick={() => onOpenMember(u.user_id)} className="w-full flex items-center gap-3 py-2 text-left hover:bg-zinc-900 px-2 rounded" data-testid={`member-row-${i}`}>
            {u.main_photo ? <img src={u.main_photo} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-sm">{u.name?.[0]}</div>}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{u.name}{u.age ? `, ${u.age}` : ""} {u.is_premium && <Crown className="inline w-3.5 h-3.5 text-yellow-400" />} {u.is_banned && <Ban className="inline w-3.5 h-3.5 text-red-400" />}</p>
              <p className="text-xs text-zinc-500 truncate">{u.email} · {u.city || "—"}</p>
            </div>
            <div className="text-right text-xs shrink-0">
              <p className="text-yellow-400 font-semibold">{u.credits ?? 0} crédits</p>
              <p className="text-zinc-500">inscrit {fmtShort(u.created_at)}</p>
            </div>
          </button>
        ))}
      </div>
      <div className="flex justify-center gap-3 mt-3 text-sm">
        <button disabled={page === 0} onClick={() => setPage(page - 1)} className="px-3 py-1 rounded bg-zinc-900 disabled:opacity-40" data-testid="members-prev">← Précédent</button>
        <span className="text-zinc-500">Page {page + 1} / {Math.max(1, Math.ceil(data.total / 50))}</span>
        <button disabled={(page + 1) * 50 >= data.total} onClick={() => setPage(page + 1)} className="px-3 py-1 rounded bg-zinc-900 disabled:opacity-40" data-testid="members-next">Suivant →</button>
      </div>
    </div>
  );
}

export function PurchasesTab({ onOpenMember }) {
  const [txs, setTxs] = useState(null);
  useEffect(() => { fetch(`${API}/admin/transactions`, { headers: authHeaders() }).then((r) => r.json()).then(setTxs); }, []);
  if (!txs) return <Loader2 className="w-6 h-6 animate-spin text-pink-500 mx-auto my-10" />;
  return (
    <div className="p-3 overflow-x-auto" data-testid="admin-purchases">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-zinc-500"><th className="py-2">Date</th><th>Membre</th><th>Produit</th><th>Source</th><th>Crédits</th><th>Statut</th></tr></thead>
        <tbody>
          {txs.map((t, i) => (
            <tr key={t.transaction_id || i} className="border-t border-zinc-900 hover:bg-zinc-900 cursor-pointer" onClick={() => t.user_id && onOpenMember(t.user_id)} data-testid={`tx-row-${i}`}>
              <td className="py-2 text-zinc-400 whitespace-nowrap">{fmtShort(t.created_at)}</td>
              <td>{t.user?.name || "—"} <span className="text-zinc-500 text-xs">{t.user?.email}</span></td>
              <td>{t.sku || t.type || t.package || "—"}</td>
              <td className="text-zinc-400">{t.source || "—"}</td>
              <td>{t.credits ?? t.credits_added ?? ""}</td>
              <td className={t.status === "completed" ? "text-emerald-400" : "text-zinc-400"}>{t.status || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {txs.length === 0 && <p className="text-zinc-500 text-sm p-4">Aucune transaction.</p>}
    </div>
  );
}
